import React, { useEffect, useState } from "react";
import { Star, MessageCircle, Phone, Video, Clock, Award } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { useSettings } from "@/context/SettingsContext";

export default function Astrologers() {
  const { waLink } = useSettings();
  const [role, setRole] = useState("astrologer");
  const [list, setList] = useState([]);
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    setLoading(true);
    api.get(`/providers?role=${role}`).then((r) => { setList(r.data); setLoading(false); });
  }, [role]);

  const book = async (provider, service) => {
    if (!user || user === false) { nav("/login"); return; }
    try {
      const scheduled_at = new Date(Date.now() + 30 * 60 * 1000).toISOString();
      const amount = role === "astrologer" ? provider.profile.rate_per_min * 15 : 2100;
      await api.post("/bookings", {
        service_type: service, provider_id: provider.id,
        scheduled_at, notes: "Booked via provider profile", amount,
      });
      toast.success(`Booking confirmed with ${provider.name}`);
      setBooking(null);
    } catch (e) {
      toast.error("Booking failed. Try again.");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-16">
      <div className="mb-10">
        <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-3">Consult</div>
        <h1 className="font-serif text-4xl md:text-5xl tracking-tight max-w-3xl">
          Verified gurus, one tap <span className="italic gold-gradient">away from home.</span>
        </h1>
      </div>

      <div className="flex gap-2 mb-8">
        {["astrologer","pandit"].map((r) => (
          <button
            key={r}
            data-testid={`role-tab-${r}`}
            onClick={() => setRole(r)}
            className={`px-5 py-2 rounded-full text-sm border transition ${
              role === r ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]"
                         : "border-white/15 text-[#A0A4AB] hover:border-white/30"
            }`}
          >
            {r === "astrologer" ? "Astrologers" : "Pandit Ji"}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-[#A0A4AB]">Loading verified gurus…</div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((p) => (
            <div key={p.id} data-testid={`provider-card-${p.id}`} className="p-6 rounded-xl bg-[#0A0C10] border border-white/10 hover:border-[#D4AF37]/40 transition-colors">
              <div className="flex gap-4">
                <div className="relative w-20 h-20 rounded-full overflow-hidden border border-[#D4AF37]/40 flex-none">
                  <img src={p.profile.photo} className="w-full h-full object-cover" alt={p.name} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-xl truncate">{p.name}</div>
                  <div className="flex items-center gap-3 mt-1 text-xs text-[#A0A4AB]">
                    <span className="flex items-center gap-1 text-[#D4AF37]"><Star size={12} fill="#D4AF37" /> {p.profile.rating}</span>
                    <span>·</span>
                    <span>{p.profile.sessions}+ sessions</span>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-[#A0A4AB] mt-1">
                    <Award size={12} /> {p.profile.experience_years} yrs
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-1.5 mt-4">
                {p.profile.expertise.map((e) => (
                  <span key={e} className="text-[10px] uppercase tracking-widest px-2 py-1 rounded bg-[#12151C] border border-white/10 text-[#D4AF37]/80">{e}</span>
                ))}
              </div>

              <p className="text-sm text-[#A0A4AB] mt-4 leading-relaxed line-clamp-3">{p.profile.bio}</p>

              <div className="flex items-center justify-between mt-5 pt-4 border-t border-white/10">
                {role === "astrologer" ? (
                  <div>
                    <div className="font-serif text-lg text-[#D4AF37]">₹{p.profile.rate_per_min}/min</div>
                    <div className="text-[10px] uppercase tracking-widest text-[#6E737D]">Consult Rate</div>
                  </div>
                ) : (
                  <div>
                    <div className="font-serif text-lg text-[#D4AF37]">₹2,100+</div>
                    <div className="text-[10px] uppercase tracking-widest text-[#6E737D]">Per Pooja</div>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <a
                    href={waLink(
                      `Namaste 🙏  I would like to book ${p.name} on Vedacosmos for a ${role === "astrologer" ? "consultation" : "pooja / yagya"}. Please share available slots.`
                    )}
                    target="_blank" rel="noopener noreferrer"
                    data-testid={`wa-book-${p.id}`}
                    className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-emerald-500/10 border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/20 transition"
                    title="Book via WhatsApp"
                    aria-label="Book via WhatsApp"
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                  </a>
                  <button
                    data-testid={`book-provider-${p.id}`}
                    onClick={() => setBooking({ provider: p, role })}
                    className="px-4 py-2 text-sm rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#E5C865] transition-colors"
                  >
                    Book
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {booking && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center px-6" onClick={() => setBooking(null)}>
          <div className="bg-[#0A0C10] border border-white/10 rounded-xl p-8 max-w-md w-full" onClick={(e)=>e.stopPropagation()}>
            <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-2">Confirm Booking</div>
            <h3 className="font-serif text-2xl">{booking.provider.name}</h3>
            <p className="text-sm text-[#A0A4AB] mt-2">Choose your consultation type — session starts within 30 minutes.</p>
            <div className="grid grid-cols-3 gap-2 mt-6">
              {booking.role === "astrologer" ? (
                <>
                  <button onClick={() => book(booking.provider, "astrology")} data-testid="book-chat-btn" className="p-4 rounded-lg border border-white/10 hover:border-[#D4AF37] text-center transition">
                    <MessageCircle className="mx-auto text-[#D4AF37]" size={18} />
                    <div className="text-xs mt-2">Chat</div>
                  </button>
                  <button onClick={() => book(booking.provider, "astrology")} data-testid="book-call-btn" className="p-4 rounded-lg border border-white/10 hover:border-[#D4AF37] text-center transition">
                    <Phone className="mx-auto text-[#D4AF37]" size={18} />
                    <div className="text-xs mt-2">Voice</div>
                  </button>
                  <button onClick={() => book(booking.provider, "astrology")} data-testid="book-video-btn" className="p-4 rounded-lg border border-white/10 hover:border-[#D4AF37] text-center transition">
                    <Video className="mx-auto text-[#D4AF37]" size={18} />
                    <div className="text-xs mt-2">Video</div>
                  </button>
                </>
              ) : (
                <>
                  <button onClick={() => book(booking.provider, "pandit_pooja")} data-testid="book-pooja-btn" className="p-4 rounded-lg border border-white/10 hover:border-[#D4AF37] text-center transition col-span-1">
                    <div className="text-lg text-[#D4AF37]">🪔</div><div className="text-xs mt-2">Pooja</div>
                  </button>
                  <button onClick={() => book(booking.provider, "yagya")} data-testid="book-yagya-btn" className="p-4 rounded-lg border border-white/10 hover:border-[#D4AF37] text-center transition">
                    <div className="text-lg text-[#D4AF37]">🔥</div><div className="text-xs mt-2">Yagya</div>
                  </button>
                  <button onClick={() => book(booking.provider, "spiritual_tour")} data-testid="book-tour-btn" className="p-4 rounded-lg border border-white/10 hover:border-[#D4AF37] text-center transition">
                    <div className="text-lg text-[#D4AF37]">🕉️</div><div className="text-xs mt-2">Path</div>
                  </button>
                </>
              )}
            </div>
            <button onClick={() => setBooking(null)} className="mt-6 w-full py-2 text-sm text-[#A0A4AB] hover:text-[#EAE6DF]">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
